/**
 * Dev Tools Component
 *
 * Top-level, region-agnostic API testing page. The user picks a region
 * (drives `x-region` for every call) and optionally supplies a role ID to
 * prefill form fields from an organisation. Both selections are reflected
 * in the URL via query params (`?region=&roleId=`) so the page is
 * shareable and refresh-safe.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CheckCircleOutline as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { regionValuesArr } from "api";
import { useHistory, useLocation } from "react-router-dom";

import { Role } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { EnhancedResponseViewer } from "./components/enhanced-response-viewer";
import { DevToolsErrorBoundary } from "./components/error-boundary";
import { FormFieldRenderer } from "./components/form-field-renderer";
import { MethodSelector } from "./components/method-selector";
import { ServiceSelector } from "./components/service-selector";
import { TabPanel } from "./components/tab-panel";
import { MethodNames } from "./constants";
import { MethodExecutionService, SchemaService } from "./services";
import { FormState, MethodDefinition, ServiceInfo } from "./types";
import {
  buildRequestObject,
  createEmptyFormState,
  prefillFormWithOrgData,
  updateFormField,
  validateField,
} from "./utils/form-utils";
import { logger } from "./utils/logger";
import { getOrgWithSubscription } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";

type LocationState = {
  orgName?: string;
};

const ALL_REGIONS = ["au", "us", "eu"] as const;
type Region = typeof ALL_REGIONS[number];
type RegionSelection = Region | "all";
const REGIONS = ALL_REGIONS.filter((r) =>
  regionValuesArr.includes(r)
) as unknown as typeof ALL_REGIONS;
const DEFAULT_REGION: RegionSelection = (REGIONS[0] ?? "au") as RegionSelection;

const isRegionSelection = (value: string | null): value is RegionSelection =>
  value !== null && ([...REGIONS, "all"] as string[]).includes(value);

const SupportServiceForm: React.FC = () => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const orgName = location.state?.orgName ?? "";

  // Read initial region + roleId from the URL query string. Both optional.
  const initialParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const queryRegion = params.get("region");
    return {
      region: isRegionSelection(queryRegion) ? queryRegion : DEFAULT_REGION,
      roleId: params.get("roleId") ?? "",
    };
    // We deliberately only read the search string on first mount; afterwards
    // the form is the source of truth and we write back to the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [region, setRegion] = useState<RegionSelection>(initialParams.region);
  const [roleIdInput, setRoleIdInput] = useState<string>(initialParams.roleId);
  const [appliedRoleId, setAppliedRoleId] = useState<string>(
    initialParams.roleId
  );
  const [prefillSource, setPrefillSource] = useState<Role | null>(null);
  const [isLoadingPrefill, setIsLoadingPrefill] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);

  const [availableServices, setAvailableServices] = useState<ServiceInfo[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>("support");
  const [methods, setMethods] = useState<MethodDefinition[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<MethodNames | "">("");
  const [formState, setFormState] = useState<FormState>({
    data: {},
    errors: {},
    touched: {},
    isValid: false,
    isSubmitting: false,
  });
  const [response, setResponse] = useState<unknown>(null);
  const [multiRegionResponses, setMultiRegionResponses] = useState<Record<
    Region,
    unknown
  > | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [multiRegionTab, setMultiRegionTab] = useState(0);
  const [resetSnackbar, setResetSnackbar] = useState(false);
  const [copied, setCopied] = useState(false);

  const selectedMethodDef = useMemo(
    () => methods.find((m) => m.name === selectedMethod),
    [methods, selectedMethod]
  );

  const requestObject = useMemo(
    () => buildRequestObject(formState.data, selectedMethodDef?.inputSchema),
    [formState.data, selectedMethodDef]
  );

  // Sync region + applied role id back into the URL so refresh + copy-link both
  // work. history.replace (not push) so the back button doesn't fill with
  // intermediate states. Skip the very first render — the URL already matches
  // by construction.
  const isFirstSync = useRef(true);
  useEffect(() => {
    if (isFirstSync.current) {
      isFirstSync.current = false;
      return;
    }
    const params = new URLSearchParams();
    if (region !== DEFAULT_REGION) params.set("region", region);
    if (appliedRoleId) params.set("roleId", appliedRoleId);
    const search = params.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
      state: location.state,
    });
    // location.pathname/search/state are read intentionally without depending
    // on `location` itself, which would cause an infinite loop after replace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, appliedRoleId, history]);

  // Load available services
  useEffect(() => {
    try {
      const services = SchemaService.getAvailableServices();
      setAvailableServices(services);
    } catch (error) {
      logger.error("Failed to load available services", { error });
    }
  }, []);

  // Load methods when service changes
  useEffect(() => {
    const loadMethods = async () => {
      try {
        const methodDefinitions =
          await SchemaService.getMethodDefinitionsForService(selectedServiceId);
        setMethods(methodDefinitions);
        if (selectedMethod) setResetSnackbar(true);
        setSelectedMethod("");
        setResponse(null);
        setMultiRegionResponses(null);
      } catch (error) {
        logger.error(
          `Failed to load methods for service ${selectedServiceId}`,
          { error }
        );
        setMethods([]);
      }
    };

    if (selectedServiceId) {
      loadMethods();
    }
  }, [selectedServiceId]);

  // Fetch the prefill source whenever an applied role id changes (initial
  // mount with query param, or explicit Apply click). Empty roleId clears.
  useEffect(() => {
    if (!appliedRoleId) {
      setPrefillSource(null);
      return;
    }
    let cancelled = false;
    setIsLoadingPrefill(true);
    getOrgWithSubscription(
      { roleId: appliedRoleId },
      callOptionsHeaders(region === "all" ? REGIONS[0] : region, appliedRoleId)
    )
      .then((response) => {
        if (cancelled) return;
        setPrefillSource(response.role ?? null);
      })
      .catch((error) => {
        if (cancelled) return;
        logger.error("Failed to load prefill source", { error });
        setPrefillSource(null);
        setPrefillError(
          "Could not find org — check the role ID and try again."
        );
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingPrefill(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appliedRoleId, region]);

  // Prefill form when method or prefill source changes
  useEffect(() => {
    if (!selectedMethod || methods.length === 0) return;
    const method = methods.find((m) => m.name === selectedMethod);
    if (!method) return;

    let initialState = createEmptyFormState();

    const effectiveOrgData = prefillSource?.id
      ? prefillSource
      : appliedRoleId
      ? ({ id: appliedRoleId } as Role)
      : null;

    if (effectiveOrgData?.id && method.inputSchema) {
      try {
        initialState = prefillFormWithOrgData(
          initialState,
          effectiveOrgData,
          method.inputSchema
        );
      } catch (error) {
        logger.error("Error during prefill", { error });
      }
    }

    setFormState(initialState);
  }, [selectedMethod, methods, prefillSource, appliedRoleId]);

  const handleApplyRoleId = () => {
    setPrefillError(null);
    setAppliedRoleId(roleIdInput.trim());
  };

  const handleClearRoleId = () => {
    setRoleIdInput("");
    setAppliedRoleId("");
    setPrefillSource(null);
    setPrefillError(null);
  };

  // Event handlers with enhanced error handling
  const handleInputChange = useCallback(
    (fieldName: string, value: unknown) => {
      setFormState((prevState) => {
        try {
          const updatedState = updateFormField(prevState, fieldName, value);

          return updatedState;
        } catch (error) {
          logger.error("Error in handleInputChange", { error });
          return prevState;
        }
      });
    },
    [methods, selectedMethod]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMethod) return;

    setFormState((prev) => ({ ...prev, isSubmitting: true }));
    setMultiRegionTab(0);

    try {
      if (!selectedMethodDef) {
        throw new Error(
          `Method ${selectedMethod} not found or improperly configured`
        );
      }

      if (region === "all") {
        const results = await Promise.all(
          REGIONS.map((r) =>
            MethodExecutionService.executeMethodForService(
              selectedServiceId,
              selectedMethod,
              requestObject,
              { region: r, referenceId: appliedRoleId || undefined }
            )
          )
        );
        setMultiRegionResponses(
          Object.fromEntries(REGIONS.map((r, i) => [r, results[i]])) as Record<
            Region,
            unknown
          >
        );
        setResponse(null);
      } else {
        const result = await MethodExecutionService.executeMethodForService(
          selectedServiceId,
          selectedMethod,
          requestObject,
          { region, referenceId: appliedRoleId || undefined }
        );
        setResponse(result);
        setMultiRegionResponses(null);
      }
      setActiveTab(1);
    } catch (error) {
      logger.error("Method execution error", { error });
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setResponse({
        error: errorMessage,
        details: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
      });
      // Switch to Response tab even on error to show the error
      setActiveTab(1);
    } finally {
      setFormState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleCopyRequest = async () => {
    try {
      const requestJson = JSON.stringify(requestObject, null, 2);
      await navigator.clipboard.writeText(requestJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error("Failed to copy to clipboard", { error });
    }
  };

  const renderMethodInputs = () => {
    const selectedMethodDef = methods.find(
      (method) => method.name === selectedMethod
    );
    if (!selectedMethodDef?.inputSchema) return null;

    return (
      <FormFieldRenderer
        fields={selectedMethodDef.inputSchema}
        formState={formState}
        onFieldChange={handleInputChange}
      />
    );
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ paddingBottom: "16px" }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "stretch",
                flexWrap: "wrap",
              }}
            >
              <Paper
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0,
                  borderRadius: 1.5,
                  backgroundColor: "action.hover",
                }}
                elevation={0}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Region
                </Typography>
                <ToggleButtonGroup
                  value={region}
                  exclusive
                  onChange={(_, value) => {
                    if (value !== null) setRegion(value);
                  }}
                  size="small"
                  aria-label="region"
                  data-testid="dev-tools-region-select"
                  sx={{
                    gap: 0.5,
                    "& .MuiToggleButtonGroup-grouped": {
                      border: "none !important",
                      borderRadius: "6px !important",
                      px: 1.5,
                      py: 0.25,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "text.secondary",
                      backgroundColor: "transparent",
                      "&.Mui-selected": {
                        backgroundColor: "background.paper",
                        color: "text.primary",
                        fontWeight: 600,
                        boxShadow: 1,
                        "&:hover": { backgroundColor: "background.paper" },
                      },
                    },
                  }}
                >
                  {REGIONS.map((r) => (
                    <ToggleButton
                      key={r}
                      value={r}
                      aria-label={r}
                      disableRipple
                    >
                      {region === r && (
                        <Box
                          component="span"
                          sx={{
                            color: "primary.main !important",
                            mr: 0.5,
                            fontSize: "0.5rem",
                          }}
                        >
                          ●
                        </Box>
                      )}
                      {r.toUpperCase()}
                    </ToggleButton>
                  ))}
                  <ToggleButton value="all" aria-label="all" disableRipple>
                    {region === "all" && (
                      <Box
                        component="span"
                        sx={{
                          color: "primary.main !important",
                          mr: 0.5,
                          fontSize: "0.5rem",
                        }}
                      >
                        ●
                      </Box>
                    )}
                    All
                  </ToggleButton>
                </ToggleButtonGroup>
              </Paper>

              <TextField
                size="small"
                label="Prefill from role ID (optional)"
                value={roleIdInput}
                onChange={(event) => setRoleIdInput(event.target.value)}
                onBlur={() => {
                  if (roleIdInput.trim() !== appliedRoleId) handleApplyRoleId();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleApplyRoleId();
                  }
                }}
                sx={{ minWidth: 320, flexGrow: 1 }}
                inputProps={{ "data-testid": "dev-tools-role-id-input" }}
              />

              <Button
                variant="outlined"
                size="small"
                onClick={handleApplyRoleId}
                disabled={
                  isLoadingPrefill ||
                  roleIdInput.trim() === "" ||
                  (roleIdInput.trim() === appliedRoleId && !!prefillSource)
                }
                color={
                  roleIdInput.trim() === appliedRoleId && !!prefillSource
                    ? "success"
                    : "primary"
                }
                startIcon={
                  roleIdInput.trim() === appliedRoleId && !!prefillSource ? (
                    <CheckCircleIcon />
                  ) : null
                }
                data-testid="dev-tools-apply-role-id"
              >
                {isLoadingPrefill ? (
                  <CircularProgress size={16} />
                ) : roleIdInput.trim() === appliedRoleId && !!prefillSource ? (
                  "Applied"
                ) : (
                  "Apply"
                )}
              </Button>

              {(appliedRoleId || prefillSource) && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClearRoleId}
                  data-testid="dev-tools-clear-role-id"
                >
                  Clear
                </Button>
              )}

              {prefillSource?.name && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Prefilling from ${prefillSource.name}`}
                    data-testid="dev-tools-prefill-source-chip"
                  />
                </Box>
              )}
              {prefillError && (
                <Alert
                  severity="error"
                  sx={{ py: 0, px: 1 }}
                  onClose={() => setPrefillError(null)}
                >
                  {prefillError}
                </Alert>
              )}
            </Box>
          </Paper>

          <form onSubmit={handleSubmit}>
            {/* Service and Method Selection */}
            <Box
              sx={{ display: "flex", gap: 2, mb: 1, alignItems: "flex-start" }}
            >
              <ServiceSelector
                selectedServiceId={selectedServiceId}
                onServiceChange={setSelectedServiceId}
                services={availableServices}
              />

              <MethodSelector
                methods={methods}
                selectedMethod={selectedMethod}
                onMethodChange={(methodName) =>
                  setSelectedMethod(methodName as MethodNames)
                }
              />
            </Box>

            {/* Side-by-side layout for Form and Results */}
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Left side - Form Input */}
              <Grid item xs={12} md={5}>
                <Paper
                  variant="outlined"
                  sx={{
                    padding: 2,
                    minHeight: selectedMethod ? 400 : "auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {selectedMethod && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ lineHeight: 1 }}
                      >
                        Request
                      </Typography>
                      {selectedMethodDef &&
                        selectedMethodDef.inputSchema.length > 0 && (
                          <Chip
                            label={`${selectedMethodDef.inputSchema.length} fields`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                    </Box>
                  )}
                  <Box sx={{ mb: 1 }}>
                    {isLoadingPrefill ? (
                      <Box sx={{ py: 2, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Loading prefill source…
                        </Typography>
                      </Box>
                    ) : (
                      renderMethodInputs()
                    )}
                  </Box>

                  <Tooltip
                    title={!selectedMethod ? "Select a method first" : ""}
                  >
                    <span>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={formState.isSubmitting || !selectedMethod}
                        startIcon={
                          formState.isSubmitting ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : null
                        }
                        sx={{ mt: 1 }}
                      >
                        {formState.isSubmitting
                          ? "Submitting..."
                          : "Execute Method"}
                      </Button>
                    </span>
                  </Tooltip>
                </Paper>
              </Grid>

              {/* Right side - Results */}
              <Grid item xs={12} md={7}>
                <Paper
                  sx={{
                    padding: 2,
                    minHeight: 400,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                      value={activeTab}
                      onChange={(event, newValue) => setActiveTab(newValue)}
                    >
                      {selectedMethod && <Tab label="Request Preview" />}
                      <Tab label="Response" />
                    </Tabs>
                  </Box>

                  <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                    {/* Request Preview Tab - Shows current form state */}
                    {selectedMethod && (
                      <TabPanel value={activeTab} index={0}>
                        <Box sx={{ textAlign: "left" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1.5,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{ mb: 0.5, fontWeight: 600 }}
                              >
                                Request Preview
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                JSON request that will be sent to the API
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                alignItems: "center",
                              }}
                            >
                              {(() => {
                                const fieldCount =
                                  Object.keys(requestObject).length;
                                const requestSize =
                                  fieldCount > 0
                                    ? new Blob([JSON.stringify(requestObject)])
                                        .size
                                    : 0;
                                const sizeLabel =
                                  requestSize < 1024
                                    ? `${requestSize} B`
                                    : `${(requestSize / 1024).toFixed(1)} KB`;

                                return fieldCount > 0 ? (
                                  <>
                                    <Chip
                                      label={`${fieldCount} field${
                                        fieldCount !== 1 ? "s" : ""
                                      }`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={sizeLabel}
                                      size="small"
                                      color="default"
                                      variant="outlined"
                                    />
                                    <Tooltip
                                      title={
                                        copied ? "Copied!" : "Copy to clipboard"
                                      }
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={handleCopyRequest}
                                        sx={{ ml: 0.5 }}
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : null;
                              })()}
                            </Box>
                          </Box>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "grey.900"
                                  : "grey.50",
                              border: (theme) =>
                                `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <pre
                              style={{
                                margin: 0,
                                fontFamily: "monospace",
                                fontSize: "0.875rem",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",

                                overflow: "auto",
                                color: "inherit",
                              }}
                            >
                              {(() => {
                                const hasData =
                                  Object.keys(requestObject).length > 0;

                                return hasData
                                  ? JSON.stringify(requestObject, null, 2)
                                  : "// No data entered yet.\n// Fill out the form fields to see the request preview.";
                              })()}
                            </pre>
                          </Paper>
                        </Box>
                      </TabPanel>
                    )}

                    {/* Response Tab */}
                    <TabPanel value={activeTab} index={selectedMethod ? 1 : 0}>
                      {formState.isSubmitting ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Executing method...
                          </Typography>
                        </Box>
                      ) : multiRegionResponses !== null ? (
                        <Box>
                          <Tabs
                            value={multiRegionTab}
                            onChange={(_, v) => setMultiRegionTab(v)}
                            sx={{
                              mb: 2,
                              minHeight: 36,
                              "& .MuiTab-root": { minHeight: 36, py: 0.5 },
                            }}
                          >
                            {REGIONS.map((r) => (
                              <Tab key={r} label={r.toUpperCase()} />
                            ))}
                          </Tabs>
                          {REGIONS.map((r, i) => (
                            <Box key={r} hidden={multiRegionTab !== i}>
                              <EnhancedResponseViewer
                                response={multiRegionResponses[r]}
                              />
                            </Box>
                          ))}
                        </Box>
                      ) : response !== null ? (
                        <EnhancedResponseViewer response={response} />
                      ) : (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Execute a method to see the response here
                          </Typography>
                        </Box>
                      )}
                    </TabPanel>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
      <Snackbar
        open={resetSnackbar}
        autoHideDuration={3000}
        onClose={() => setResetSnackbar(false)}
        message="Form reset — service changed"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

/**
 * Main Dev Tools Component with Error Boundary
 *
 * Wraps the core functionality in an error boundary for production safety
 */
const DevToolsWithErrorBoundary: React.FC = () => {
  return (
    <DevToolsErrorBoundary>
      <SupportServiceForm />
    </DevToolsErrorBoundary>
  );
};

export default DevToolsWithErrorBoundary;
